/**
 * Room on this device for maps and their attachments
 * (docs/plans/2026-09-29-attachments-move-with-their-map.md):
 *
 * - one limit per map, its content plus its attachments: 200 MB, the same as
 *   online;
 * - in the browser, a margin of the quota the browser grants: storing stops
 *   when less than 10 % of it would stay free;
 * - asking the browser to keep what Orbital stores (`persist()`), and telling
 *   the user when it may clear it.
 *
 * The decisions are pure functions of their inputs (tested): `refusal` for
 * files that exist nowhere else, `copiesToDrop` for offline copies.
 */
import { listStoredAttachments, readDocSize, deleteStoredAttachment } from '../storage/index.js';
import { t } from '../../shared/i18n/index.js';
import { emit, Events } from '../core/events.js';

export const MAP_LIMIT = 200 * 1024 * 1024;
export const QUOTA_MARGIN = 0.10;

/**
 * Why `adding` bytes can't be stored, or null when they can.
 * @param {{mapBytes: number, adding: number, usage?: number|null, quota?: number|null}} input
 * @returns {null | {reason: 'map-full'|'storage-low', text: string}}
 */
export function refusal({ mapBytes, adding, usage = null, quota = null }) {
  if (mapBytes + adding > MAP_LIMIT) {
    return { reason: 'map-full', text: t('vault.device.mapFull', { limit: formatMB(MAP_LIMIT) }) };
  }
  if (quota && usage != null && quota - usage - adding < quota * QUOTA_MARGIN) {
    return { reason: 'storage-low', text: t('vault.device.storageLow') };
  }
  return null;
}

function formatMB(bytes) {
  return `${Math.round(bytes / (1024 * 1024))} MB`;
}

/** A map's content plus its stored attachments in one scope, in bytes. */
export async function mapBytesOnDevice(scope, docId) {
  let files = 0;
  try {
    files = (await listStoredAttachments(scope, docId)).reduce((sum, file) => sum + (file.size || 0), 0);
  } catch { /* nothing readable yet */ }
  let content = 0;
  try { content = (await readDocSize(docId)) || 0; } catch { /* unreadable: counted as empty */ }
  return files + content;
}

async function browserEstimate() {
  if (typeof navigator === 'undefined' || !navigator.storage?.estimate) return { usage: null, quota: null };
  try {
    const { usage, quota } = await navigator.storage.estimate();
    return { usage: usage ?? null, quota: quota ?? null };
  } catch {
    return { usage: null, quota: null };
  }
}

const isDesktop = () => typeof window !== 'undefined' && typeof window.__TAURI_INTERNALS__ !== 'undefined';

/**
 * Throws an Error with a user-facing message when `adding` bytes for this map
 * can't be stored on the device. The desktop app has no browser quota.
 */
export async function assertRoomFor(scope, docId, adding) {
  const mapBytes = await mapBytesOnDevice(scope, docId);
  const { usage, quota } = isDesktop() ? { usage: null, quota: null } : await browserEstimate();
  const refused = refusal({ mapBytes, adding, usage, quota });
  if (refused) {
    const error = new Error(refused.text);
    error.code = refused.reason;
    throw error;
  }
}

/**
 * Offline copies to drop, oldest use first, until `needed` bytes are freed.
 * Pure (tested). Offline copies of online maps are the only thing the app
 * ever drops: the server has them.
 * @param {Array<{id: string, size: number, usedAt: number, docId?: string}>} copies
 * @returns {Array} the copies to drop
 */
export function copiesToDrop(copies, needed) {
  const drop = [];
  let freed = 0;
  for (const copy of [...copies].sort((a, b) => (a.usedAt || 0) - (b.usedAt || 0))) {
    if (freed >= needed) break;
    drop.push(copy);
    freed += copy.size || 0;
  }
  return freed >= needed ? drop : [];
}

/** Offline copies of every map in this browser (web only; empty elsewhere). */
async function allOfflineCopies() {
  if (isDesktop() || typeof indexedDB === 'undefined' || !indexedDB.databases) return [];
  const prefix = 'orbital-offline-attachments-';
  const docIds = (await indexedDB.databases()).map((db) => db.name || '')
    .filter((name) => name.startsWith(prefix)).map((name) => name.slice(prefix.length));
  const copies = [];
  for (const docId of docIds) {
    try {
      for (const file of await listStoredAttachments('offline', docId)) copies.push({ ...file, docId });
    } catch { /* unreadable map: skipped */ }
  }
  return copies;
}

/**
 * Make room on this device for `adding` bytes of an online map: its content,
 * its offline copies and its files waiting to be sent share the map's 200 MB,
 * and the browser's quota keeps its margin. Offline copies are dropped to
 * make room (the oldest used first: this map's for its 200 MB, then any
 * map's for the quota); files waiting to be sent never are.
 *
 * @param {{evict?: boolean, waitingBytes?: number}} options evict false: use
 *   free room only (background copies never push out anything);
 *   waitingBytes: this map's files waiting to be sent
 * @returns {Promise<null | {reason: 'map-full'|'storage-low', text: string}>}
 *   null when there is room, otherwise why not
 */
export async function makeRoomOnDevice(docId, adding, { evict = true, waitingBytes = 0 } = {}) {
  const own = await listStoredAttachments('offline', docId).catch(() => []);
  let content = 0;
  try { content = (await readDocSize(docId)) || 0; } catch { /* unreadable: counted as empty */ }
  const total = content + waitingBytes + own.reduce((sum, file) => sum + (file.size || 0), 0);
  if (total + adding > MAP_LIMIT) {
    const drop = evict ? copiesToDrop(own, total + adding - MAP_LIMIT) : [];
    if (!drop.length) return refusal({ mapBytes: total, adding });
    for (const copy of drop) await deleteStoredAttachment('offline', docId, copy.id);
  }
  if (isDesktop()) return null;
  const { usage, quota } = await browserEstimate();
  if (!quota || usage == null) return null;
  const short = adding + quota * QUOTA_MARGIN - (quota - usage);
  if (short <= 0) return null;
  const drop = evict ? copiesToDrop(await allOfflineCopies(), short) : [];
  if (!drop.length) return refusal({ mapBytes: 0, adding, usage, quota });
  for (const copy of drop) await deleteStoredAttachment('offline', copy.docId, copy.id);
  return null;
}

/**
 * Make room for an offline copy. Returns false when the copy should not be
 * kept: it is only a copy, so nothing is refused to the user.
 */
export async function makeRoomForOfflineCopy(docId, adding, options) {
  return !(await makeRoomOnDevice(docId, adding, options));
}

let persistence = null; // Promise<boolean>: whether the browser keeps our data
let clearRisk = null; // last known answer of storageMayBeCleared (null: not asked yet)

/**
 * Whether the browser may delete what Orbital stores, as last checked
 * (null before the first check). The warnings read it synchronously;
 * Events.STORAGE_POLICY_CHANGED says when it changes.
 */
export function knownClearRisk() {
  return clearRisk;
}

function noteClearRisk(value) {
  if (clearRisk === value) return;
  clearRisk = value;
  emit(Events.STORAGE_POLICY_CHANGED, { mayBeCleared: value });
}

/**
 * Ask the browser once to keep what Orbital stores (not cleared under
 * storage pressure or after days without a visit). Called before storing
 * anything that exists nowhere else, never awaited there: Firefox shows a
 * prompt and answers only once the user does (a file added to a map on the
 * device waited for it). The answer drives the "may be deleted" warnings
 * (STORAGE_POLICY_CHANGED). The desktop app keeps files on disk.
 * @returns {Promise<boolean>} true when the browser agreed (or on desktop)
 */
export function ensurePersistentStorage() {
  if (isDesktop()) return Promise.resolve(true);
  if (!persistence) {
    persistence = (async () => {
      const storage = typeof navigator !== 'undefined' ? navigator.storage : null;
      if (!storage?.persist) return false;
      try {
        if (await storage.persisted?.()) return true;
        return await storage.persist();
      } catch {
        return false;
      }
    })().then((kept) => {
      noteClearRisk(!kept);
      return kept;
    });
  }
  return persistence;
}

/**
 * Whether the browser may delete what Orbital stores: false on desktop or
 * once the browser agreed to keep it; checked without asking.
 */
export async function storageMayBeCleared() {
  let risk;
  if (isDesktop()) {
    risk = false;
  } else {
    const storage = typeof navigator !== 'undefined' ? navigator.storage : null;
    if (!storage?.persisted) risk = true;
    else {
      try { risk = !(await storage.persisted()); } catch { risk = true; }
    }
  }
  noteClearRisk(risk);
  return risk;
}

/** Tests only. */
export function _resetForTests() {
  persistence = null;
  clearRisk = null;
}
